import {
    OpenContentClient,
    QueryBuilder,
    QueryResponseHelper
} from '@infomaker/oc-client'
import {isObject} from 'lodash'

/**
 * ConceptService class
 *
 * A service filled with utility functions for Concept items
 * and enhanced data loading
 *
 */
class ConceptService {

    /**
     * Constructor
     *
     * @param {object} api Writer API instance
     * @param {object} config Config object from writer.json
     */
    constructor(api, config) {
        this.remoteConceptTypeConfig = {}
        this.operationHandlers = new Map()

        this.api = api
        this.contenttype = 'Concept'
        this.client = (config && config.baProxy) ? new OpenContentClient(config.baProxy) : null
        this.broaderLimit = (config && config.broaderLimit) ? config.broaderLimit : 3
        this.searchLimit = (config && config.searchLimit) ? config.searchLimit : 25
        this.allowedConceptStatuses = (config && config.allowedConceptStatuses) ? config.allowedConceptStatuses : null

        this.propertyMap = Object.assign({
            "uuid": "uuid",
            "ConceptName": "ConceptName",
            "ConceptNameString": "ConceptNameString",
            "ConceptStatus": "ConceptStatus",
            "ConceptDefinitionShort": "ConceptDefinitionShort",
            "ConceptDefinitionLong": "ConceptDefinitionLong",
            "ConceptImTypeFull": "ConceptImTypeFull",
            "ConceptImSubTypeFull": "ConceptImSubTypeFull",
            "ConceptGeometry": "ConceptGeometry",
            "ConceptAvatarUuid": "ConceptAvatarUuid",
            "ConceptAssociatedWith": "ConceptAssociatedWith",

            "ConceptBroaderRelation": "ConceptBroaderRelation",
            "ConceptAssociatedWithRelations": "ConceptAssociatedWithRelations",
            "ConceptAssociatedWithMeRelations": "ConceptAssociatedWithMeRelations",
            "ConceptReplacedByRelation": "ConceptReplacedByRelation"
        }, (config && config.propertyMap) ? config.propertyMap : {})
        this.titleSearchField = (config && config.titleSearchField) ? config.titleSearchField : this.propertyMap.ConceptName
        this.sortField = (config && config.sortField) ? config.sortField : 'ConceptNameString'

        this.responseProperties = Object.values(this.propertyMap)
        this.responseProperties.splice(this.responseProperties.indexOf(this.propertyMap.ConceptBroaderRelation), 1)
        this.responseProperties.splice(this.responseProperties.indexOf(this.propertyMap.ConceptReplacedByRelation), 1)
        this.responseProperties.splice(this.responseProperties.indexOf(this.propertyMap.ConceptAssociatedWithRelations), 1)
        this.responseProperties.splice(this.responseProperties.indexOf(this.propertyMap.ConceptAssociatedWithMeRelations), 1)

        this.responseProperties.concat().forEach(prop => {
            if (
                prop !== this.propertyMap.ConceptBroaderRelation &&
                prop !== this.propertyMap.ConceptReplacedByRelation &&
                prop !== this.propertyMap.ConceptAssociatedWithRelations &&
                prop !== this.propertyMap.ConceptAssociatedWithMeRelations &&
                prop !== '') {
                this.responseProperties.push(`${this.propertyMap.ConceptReplacedByRelation}.${prop}`)
            }
        })

        let parent = this.propertyMap.ConceptBroaderRelation
        for (let index = 0; index < this.broaderLimit; index++) {
            this.responseProperties.push(`${parent}.uuid`)
            this.responseProperties.push(`${parent}.${this.propertyMap.ConceptName}`)
            this.responseProperties.push(`${parent}.${this.propertyMap.ConceptImTypeFull}`)
            parent = `${parent}.${this.propertyMap.ConceptBroaderRelation}`
        }

        if (this.propertyMap.ConceptAssociatedWithMeRelations) {
            this.responseProperties.push(`${this.propertyMap.ConceptAssociatedWithMeRelations}.uuid`)
            this.responseProperties.push(`${this.propertyMap.ConceptAssociatedWithMeRelations}.${this.propertyMap.ConceptName}`)
            this.responseProperties.push(`${this.propertyMap.ConceptAssociatedWithMeRelations}.${this.propertyMap.ConceptImTypeFull}`)
        }

        if (this.propertyMap.ConceptAssociatedWithRelations) {
            this.responseProperties.push(`${this.propertyMap.ConceptAssociatedWithRelations}.uuid`)
            this.responseProperties.push(`${this.propertyMap.ConceptAssociatedWithRelations}.${this.propertyMap.ConceptName}`)
            this.responseProperties.push(`${this.propertyMap.ConceptAssociatedWithRelations}.${this.propertyMap.ConceptImTypeFull}`)
        }

        if (config) {
            if (config.conceptPath && config.conceptPath.slice(-1) === '/') {
                config.conceptPath = config.conceptPath.substr(0, config.conceptPath.length - 1)
            }

            this.config = config
            this.contenttype = config.contenttype || this.contenttype

            Promise.resolve(this._fetchCommonConfigurationFile()).then(commonConfig => {
                this.commonConfig = commonConfig
            })
        } else {
            console.warn('Missing concept service configuration')
        }
    }

    /**
     * Available operations to register callback handlers
     *
     * @return {{ADD: number, ADDED: number, UPDATE: number, UPDATED: number, REMOVE: number, REMOVED: number}}
     */
    get operations() {
        return {
            ADD: 1,
            ADDED: 2,
            UPDATE: 3,
            UPDATED: 4,
            REMOVE: 5,
            REMOVED: 6,
        }
    }

    /**
     * Get loaded property map
     *
     * @returns {object} PropertyMap
     */
    getPropertyMap() {
        return this.propertyMap
    }

    /**
     * Get object path on remote host
     *
     * @returns {string} url (ex: https://baproxy.dev.writer.infomaker.io:5555/objects)
     */
    getRemoteObjectsPath() {
        const { baProxy } = this.config
        return `${baProxy.protocol}${baProxy.hostName}:${baProxy.port}${baProxy.objectPath}`
    }

    /**
     * Get remote config filename for a specific concept type
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     *
     * @private
     *
     * @returns {string} fileName
     */
    _getConceptFileName(conceptItem) {
        let type = (conceptItem.type || conceptItem[this.propertyMap.ConceptImTypeFull])

        if (type.indexOf('/') !== -1) {
            type = type.substr(type.indexOf('/') + 1, type.length)
        }

        let subType = (type === 'place' || type === '/place') ? conceptItem[this.propertyMap.ConceptImSubTypeFull] ? conceptItem[this.propertyMap.ConceptImSubTypeFull] : '-position' : ''

        if (subType.indexOf('/') !== -1) {
            subType = `-${subType.substr(subType.indexOf('/') + 1, subType.length)}`
        }

        let fileName = `${type}${subType}`
        if (fileName.slice(-1) === '-') {
            fileName = fileName.substr(0, fileName.length - 1)
        }

        return fileName
    }

    /**
     * Get value for article concept link rel attribute based on concept type
     *
     * @param {string} conceptType 'x-im/xxx'
     *
     * @private
     *
     * @returns {string} rel
     */
    _getConceptRel(conceptType) {
        switch (conceptType.toLowerCase()) {
            case 'x-im/channel':
                return 'channel'

            case 'x-im/author':
                return 'author'

            default:
                return 'subject'
        }
    }

    /**
     * Function to register handlers for specific ConceptType operations
     *
     * @param {string} conceptType ex. 'x-im/category'
     * @param {number} operation ex. ConceptService.operations.ADD
     * @param {function} handler function that will be called when operation is triggered
     *
     * @example
     * ConceptService.on(
     *     'x-im/category',
     *     ConceptService.operations.ADD,
     *     this.handleNewCategory
     * )
     */
    on(conceptType, operation, handler) {
        conceptType = conceptType.replace('-', '').replace('/', '')
        const conceptTypeMap = this.operationHandlers.get(conceptType)

        conceptTypeMap ?
            conceptTypeMap.get(operation) ? conceptTypeMap.get(operation).push(handler) : conceptTypeMap.set(operation, [handler]) :
            this.operationHandlers.set(conceptType, new Map([
                [operation, [handler]]
            ]))
    }

    /**
     * Function to remove registered handler
     *
     * @param {string} conceptType ex. 'x-im/category'
     * @param {number} operation ex. ConceptService.operations.ADD
     * @param {function} handler function that was registered as callback for the operation
     *
     * @example
     * ConceptService.off(
     *     'x-im/category',
     *      ConceptService.operations.ADD,
     *      this.handleNewCategory
     * )
     */
    off(conceptType, operation, handler) {
        conceptType = conceptType.replace('-', '').replace('/', '')
        const conceptTypeHandlers = this.operationHandlers.get(conceptType)

        if (conceptTypeHandlers) {
            const handlers = conceptTypeHandlers.get(operation)

            if (handlers) {
                const index = handlers.indexOf(handler)

                if (index > -1) {
                    handlers.splice(index, 1)
                    conceptTypeHandlers.set(operation, handlers)
                }
            }
        }
    }

    /**
     * Function to check if a specific conceptType has a registered handler for a specific operation
     *
     * @param {string} conceptType ex. 'x-im/category'
     * @param {number} operation ex. 'ADD' or ConceptService.operations.ADD
     *
     * @returns {boolean} if a handler has been registered
     *
     * @example
     * if (ConceptService.has('x-im/category', ConceptService.operations.ADD)) {
     *     // Do stuff
     * }
     */
    has(conceptType, operation) {
        conceptType = conceptType.replace('-', '').replace('/', '')
        const conceptTypeHandlers = this.operationHandlers.get(conceptType)
        const eventTypeHandler = conceptTypeHandlers ? conceptTypeHandlers.get(operation) : []

        return (eventTypeHandler && eventTypeHandler.length) ? true : false
    }

    /**
     * Function to trigger registered handler for conceptType operations
     *
     * @param {number} operation ex. 'ADD' or ConceptService.operations.ADD
     * @param {object} conceptItem POJSO representation of a x-im concept
     *
     * @example
     * ConceptService.trigger(
     *     ConceptService.operations.ADD,
     *     conceptItem = {
     *         uuid: xxx,
     *         create: true,
     *         ....
     *     }
     * )
     */
    trigger(operation, conceptItem) {
        let conceptType = conceptItem.type || conceptItem[this.propertyMap.ConceptImTypeFull]

        conceptType = conceptType.replace('-', '').replace('/', '')

        const conceptTypeHandlers = this.operationHandlers.get(conceptType)
        const operationHandlers = conceptTypeHandlers ? conceptTypeHandlers.get(operation) : []

        if (operationHandlers && operationHandlers.length) {
            operationHandlers.forEach(operationHandler => {
                operationHandler(conceptItem)
            })
        }
    }

    /**
     * Get article concept by uuid
     *
     * @param {sting} uuid
     */
    getArticleConceptByUUID(uuid) {
        return this.api.newsItem.getConceptByUuid(uuid)
    }

    /**
     * Get the current article concepts
     *
     * @param {string} conceptType 'x-im/xxx'
     * @param {array} multipleTypes ex. ['x-im/xxx', 'x-im/yyy']
     * @param {array} subtypes ex. ['position', 'polygon']
     *
     * @returns {array} ConceptItems
     */
    getArticleConceptsByType(conceptType, multipleTypes, subtypes) {
        let places = []
        switch (conceptType.toLowerCase()) {

            case 'x-im/tag':
                if (Array.isArray(multipleTypes)) {
                    return this.api.newsItem.getTags(multipleTypes)
                } else {
                    return this.api.newsItem.getTags([conceptType])
                }

            case 'x-im/topic':
            case 'x-im/person':
            case 'x-im/event':
            case 'x-im/organisation':
            case 'x-im/section':
            case 'x-im/content-profile':
                return this.api.newsItem.getTags([conceptType])

            case 'x-im/author':
                return this.api.newsItem.getAuthors()

            case 'x-im/story':
                return this.api.newsItem.getStories()

            case 'x-im/category':
                return this.api.newsItem.getCategories()

            case 'x-im/channel':
                return this.api.newsItem.getNormalizedChannels()

            case 'x-im/place':
                if (Array.isArray(subtypes)) {
                    subtypes.forEach(type => {
                        places = [...places, ...this.api.newsItem.getLocations(type)]
                    })
                } else {
                    places = this.api.newsItem.getLocations('all')
                }
                return places

            default:
                return this.api.newsItem.getTags([conceptType])
        }
    }

    /**
     * Add concept link to article
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     */
    addArticleConcept(conceptItem) {
        const type = conceptItem.type || conceptItem[this.propertyMap.ConceptImTypeFull]
        const name = type.replace('-', '').replace('/', '')

        this.api.newsItem.addLink(name, {
            '@rel': conceptItem.rel || this._getConceptRel(conceptItem[this.propertyMap.ConceptImTypeFull]),
            '@title': conceptItem[this.propertyMap.ConceptName],
            '@type': conceptItem[this.propertyMap.ConceptImTypeFull],
            '@uuid': conceptItem.uuid
        }, false)

        this.api.newsItem.updateConcept(name, conceptItem, this.propertyMap)
        this.trigger(this.operations.ADDED, conceptItem)

        if (name === 'ximplace') {
            this.addExtendedGeo()
        }
    }

    /**
     * Update concept rel
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     */
    updateArticleChannelRel(conceptItem) {
        const name = conceptItem.type.replace('-', '').replace('/', '')
        this.api.newsItem.updateLinkRel(name, conceptItem)
    }

    /**
     * Update existing concept link in article
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     */
    updateArticleConcept(conceptItem) {
        const type = conceptItem.type || conceptItem[this.propertyMap.ConceptImTypeFull]
        const name = type.replace('-', '').replace('/', '')

        this.api.newsItem.updateConcept(name, conceptItem, this.propertyMap)
        this.trigger(this.operations.UPDATED, conceptItem)

        if (name === 'ximplace') {
            this.addExtendedGeo()
        }
    }

    /**
     * Remove concept link from article
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     */
    removeArticleConceptItem(conceptItem) {
        const type = conceptItem.type || conceptItem[this.propertyMap.ConceptImTypeFull]
        const name = type.replace('-', '').replace('/', '')

        this.api.newsItem.removeLinkByUUID(name, conceptItem.uuid)
        this.trigger(this.operations.REMOVED, conceptItem)

        if (name === 'ximplace') {
            this.addExtendedGeo()
        }
    }

    /**
     * Remove all links of a specific  type from the article
     *
     * @param {string} conceptType the concept type to remove from document
     */
    removeAllArticleLinksOfType(conceptType) {
        const name = conceptType.replace('-', '').replace('/', '')

        this.api.newsItem.removeAllLinksByType(name, conceptType)

        if (name === 'ximplace') {
            this.addExtendedGeo()
        }
    }

    /**
     * Add info about related Geozones in the article meta
     *
     * This will grab all polygon-links in article and combine into one OC query
     * that will get all polygons matching the configured function (Contains/Intersects/IsWithin)
     *
     */
    async addExtendedGeo() {
        if (this.config.relatedGeoFunction) {
            const existingLinks = this.getArticleConceptsByType('x-im/place', ['all'])
            let query = {type: `${this.propertyMap.ConceptImTypeFull}:x-im/place`}
            let convertedHits = []

            existingLinks.filter(link => link.data && link.data.geometry).reduce((iterator, link) => {
                iterator.not = `${iterator.not ? iterator.not : ''}${iterator.not ? ' AND' : ''} NOT uuid:${link.uuid}`
                iterator.geo = `${iterator.geo ? iterator.geo : ''}${iterator.geo ? ' OR' : ''} ${this.propertyMap.ConceptGeometry}:"${this.config.relatedGeoFunction}(${link.data.geometry})"`

                return iterator
            }, query)

            const queryBuilder = new QueryBuilder({
                q: this.config.relatedGeoExludeSelf ?
                    `${query.type} AND ${query.not} AND (${query.geo})` :
                    `${query.type} AND (${query.geo})`,
                contenttype: this.contenttype
            })
                .setStart(0)
                .setLimit(25)
                .setResponseProperties(this.responseProperties)

            const builtQuery = queryBuilder.build()
            const items = await this._fetchItems(builtQuery)

            if (items && items.length) {
                items.forEach(item => convertedHits.push(this._convertProps(item.versions[0].properties)))
            }

            this.api.newsItem.createExtendedGeoLink(convertedHits)
        }
    }

    /**
     * Recursive function to extract n number of parent names
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     * @param {boolean} [truncated=false]
     *
     * @returns {string} Broader names combine
     */
    extractBroaderText(conceptItem, truncated = false) {
        let broader = ''

        if (conceptItem[this.propertyMap.ConceptBroaderRelation] && conceptItem[this.propertyMap.ConceptBroaderRelation][this.propertyMap.ConceptName]) {
            broader = conceptItem[this.propertyMap.ConceptBroaderRelation][this.propertyMap.ConceptName]

            if (conceptItem[this.propertyMap.ConceptBroaderRelation][this.propertyMap.ConceptBroaderRelation]) {
                if (truncated) {
                    broader = `.../${broader}`
                } else {
                    broader = `${this.extractBroaderText(conceptItem[this.propertyMap.ConceptBroaderRelation])}/${broader}`
                }
            }
        }

        return broader
    }

    /**
     * Recursive function to map OC array props to POJSO
     * Will follow hierarchy props recursively
     *
     * @private
     *
     * @param {object} props
     */
    _convertProps(props) {
        return Object.keys(props).reduce((convertedObject, key) => {

            // We have value
            if (props[key] && props[key][0]) {
                if (Array.isArray(props[key])) {

                    // multivalue
                    if (props[key].length > 1) {

                        // Array with objects, recursively call self
                        if (isObject(props[key][0])) {
                            convertedObject[key] = props[key].map(obj => this._convertProps(obj))

                        // Add the array as is
                        } else {
                            convertedObject[key] = props[key]
                        }

                    // Array with only one index
                    } else {

                        // Object, recursively call self
                        if (isObject(props[key][0])) {
                            convertedObject[key] = this._convertProps(props[key][0])

                        // Add value from first index
                        } else {
                            convertedObject[key] = props[key][0]
                        }
                    }

                //Object, recursively call self
                } else if (isObject(props[key][0])) {
                    convertedObject[key] = this._convertProps(props[key][0])
                }

            // null-value, add empty string
            } else {
                convertedObject[key] = ''
            }

            return convertedObject
        }, {})
    }

    /**
     * Search ba-proxy with a pre-built query
     *
     * @param {object} builtQuery
     *
     * @private
     *
     * @returns {array} OC Response
     */
    async _fetchItems(builtQuery) {
        let items = []

        if (this.client) {
            try {
                const rawResult = await this.client.search(builtQuery)
                const jsonResult = await rawResult.json()
                items = new QueryResponseHelper(jsonResult).getItems()
            } catch (error) {
                console.warn('ConceptService: unable to fetch items')
                console.warn(error)
            }
        }

        return items
    }

    /**
     * Get the actual concept XML from storage
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     *
     * @returns {string} XML response
     */
    async getConceptItemXml(conceptItem) {
        if (conceptItem.uuid) {
            return await this.api.router.getConceptItem(conceptItem.uuid, conceptItem[this.propertyMap.ConceptImTypeFull] || conceptItem.type)
        } else {
            return await this._fetchConceptItemTemplateXml(conceptItem)
        }
    }

    /**
     * Get the concept type config, cached or remote
     *
     * @param {object} conceptItem POJSO representation of a concept
     */
    async getConceptItemConfigJson(conceptItem) {
        if (conceptItem.type || conceptItem[this.propertyMap.ConceptImTypeFull]) {
            let fileName = this._getConceptFileName(conceptItem)
            if (this.remoteConceptTypeConfig[fileName]) {
                return this.remoteConceptTypeConfig[fileName]
            } else {
                return this._fetchConceptItemConfigJson(fileName)
            }
        }

        throw new Error(`Unable to load concept service configuration: Missing type`)
    }

    /**
     * Create a new concept item in storage
     *
     * @param {string} xml
     */
    async createConceptItemXml(xml) {
        return await this.api.router.createConceptItem(xml)
    }

    /**
     * Update the actual concept XML in storage
     *
     * @param {string} uuid
     * @param {string} xml
     */
    async updateConceptItemXml(uuid, xml) {
        return await this.api.router.updateConceptItem(uuid, xml)
    }

    /**
     * Load remote common config file
     *
     * @private
     *
     * @returns {object} json
     */
    async _fetchCommonConfigurationFile() {
        const response = await fetch(`${this.config.conceptPath}/common.json`)

        if (response.ok) {
            return await response.json()
        } else {
            return {}
        }
    }

    /**
     * Fetch remote XML template
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     *
     * @private
     *
     * @returns {string} XML Response
     */
    async _fetchConceptItemTemplateXml(conceptItem) {
        let response

        if (conceptItem[this.propertyMap.ConceptImTypeFull]) {
            let fileName = this._getConceptFileName(conceptItem)
            response = await fetch(`${this.config.conceptPath}/${fileName}.xml`)

            if (response.ok) {
                const rawText = await response.text()
                return await (new window.DOMParser()).parseFromString(rawText, 'text/xml')
            }
        }

        throw new Error(`Unable to load concept template, ${(response && response.statusText) ? response.statusText : 'unknown error'}`)
    }

    /**
     * Fetch remote concept config
     *
     * @param {object} fileName JSON file containing POJSO representation of a x-im concept
     *
     * @private
     *
     * @returns {string} JSON Response
     */
    async _fetchConceptItemConfigJson(fileName) {
        let response

        if (fileName && fileName.length) {
            response = await fetch(`${this.config.conceptPath}/${fileName}.json`)
            if (response.ok) {
                const json = await response.json()
                json.common = this.commonConfig
                this.remoteConceptTypeConfig[fileName] = json

                return json
            }
        }

        throw new Error(`Unable to load concept service configuration, ${(response && response.statusText) ? response.statusText : 'unknown error'}`)
    }

    /**
     * Function to validate a concept UUID before using it to search OC
     *
     * @param {*} conceptItem
     * @returns boolean to indicate if the UUID is valid or not
     */
    hasValidUUID(conceptItem) {
        return (conceptItem.uuid && conceptItem.uuid !== '' && conceptItem.uuid !== '00000000-0000-0000-0000-000000000000')
    }

    /**
     * fetch remaining props (not stored on the article) from oc
     *
     * @param {object} conceptItem POJSO representation of a x-im concept
     * @param {object.uuid} string must be a valid OC uuid
     *
     * @example
     * import {ConceptService} from 'writer'
     *
     * ConceptService.fetchConceptItemProperties({
     *      'uuid': '12345678-1234-4567-7891-789456123456'
     * })
     *
     * @returns {object} conceptItem
     */
    async fetchConceptItemProperties(conceptItem) {
        let conceptItemResponse

        if (!this.hasValidUUID(conceptItem)) {
            return conceptItem
        }

        try {
            conceptItemResponse = await this.client.getObjectProperties(conceptItem.uuid, this.responseProperties)
        }
        catch (error) {
            console.warn('Could not fetch conceptItem properties: ', error.message)
            conceptItem.error = error.message
            return conceptItem
        }

        const conceptItemJson = await conceptItemResponse.json()

        const reduceJson = (properties) => {
            return properties.reduce((iterator, prop) => {

                if (prop.values && typeof prop.values[0] === 'object') {
                    iterator[prop.name] = []
                    prop.values.forEach(val => {
                        iterator[prop.name].push(reduceJson(val.properties))
                    })
                } else {
                    iterator[prop.name] = prop.values
                }

                return iterator
            }, {})
        }

        /**
         * After switching to OC /properties API from /search API
         * OC json response is not the same
         * To ensure backwards compatibility we need to process the json
         * by recursively running it through reduceJson(json) declared above
         */
        const arrangedProps = reduceJson(conceptItemJson.properties)

        const convertedProps = this._convertProps(arrangedProps)
        return Object.assign({}, conceptItem, convertedProps)
    }

    /**
     * Generate a query like: (ConceptImTypeFull:x-im/category OR ConceptImTypeFull:x-im/section...)
     *
     * @param {string|array} conceptTypes a concept type string, or an array of concept type strings
     *
     * @private
     *
     * @returns {string} generated query
     */
    _generateConceptTypeQuery(conceptTypes) {
        let conceptTypeQuery = ''

        if (Array.isArray(conceptTypes)) {
            conceptTypeQuery = conceptTypes.reduce((query, type) => {
                if (!query.length) {
                    query = `(${this.propertyMap.ConceptImTypeFull}:${type}`
                } else {
                    query = `${query} OR ${this.propertyMap.ConceptImTypeFull}:${type}`
                }
                return query
            }, '')
            conceptTypeQuery = conceptTypeQuery.length ? `${conceptTypeQuery})` : ''
        } else {
            conceptTypeQuery = `${this.propertyMap.ConceptImTypeFull}:${conceptTypes}`
        }

        return conceptTypeQuery
    }

    /**
     * Generate a query to specify allowed concept statuses like: (ConceptStatus:draft OR ConceptStatus:usable...)
     *
     * @param {array|string} allowedStatuses Array with allowed statuses
     *
     * @private
     *
     * @returns {string} generated query
     */
    _generateAllowedStatusesQuery(allowedStatuses) {
        let allowedStatusesQuery = ''

        if (Array.isArray(allowedStatuses) && allowedStatuses.length) {
            allowedStatusesQuery = allowedStatuses.reduce((query, status) => {
                if (!query.length) {
                    query = ` AND (${this.propertyMap.ConceptStatus}:${status}`
                } else {
                    query = `${query} OR ${this.propertyMap.ConceptStatus}:${status}`
                }

                return query
            }, allowedStatusesQuery)

            allowedStatusesQuery = allowedStatusesQuery.length ? `${allowedStatusesQuery})` : ''
        }

        return allowedStatusesQuery
    }

    /**
     * Generate a query like: AND (ConceptImSubTypeFull:x-im/person OR ConceptImSubTypeFull:x-im/topic...)
     *
     * @param {array} subtypes array of subtype strings
     *
     * @private
     *
     * @returns {string} generated query
     */
    _generateSubTypesQuery(subtypes) {
        let subtypesQuery = ''

        if (Array.isArray(subtypes)) {
            subtypesQuery = subtypes.reduce((query, subtype) => {
                if (!query.length) {
                    query = ` AND (${this.propertyMap.ConceptImSubTypeFull}:x-im/${subtype}`
                } else {
                    query = `${query} OR ${this.propertyMap.ConceptImSubTypeFull}:x-im/${subtype}`
                }
                return query
            }, '')
            subtypesQuery = subtypesQuery.length ? `${subtypesQuery})` : ''
        }

        return subtypesQuery
    }

    /**
     * Generate a query like: AND (ConceptAssociatedWith:xxx OR ConceptAssociatedWith:yyy...)
     *
     * @param {array} articleConceptsOfAssociatedType array with links from article
     *
     * @private
     *
     * @returns {string} generated query
     */
    _generateAssocQuery(articleConceptsOfAssociatedType) {
        let assocQuery = ''

        if (Array.isArray(articleConceptsOfAssociatedType)) {
            assocQuery = articleConceptsOfAssociatedType.reduce((query, link) => {
                if (!query.length) {
                    query = ` AND (${this.propertyMap.ConceptAssociatedWith}:${link.uuid}`
                } else {
                    query = `${query} OR ${this.propertyMap.ConceptAssociatedWith}:${link.uuid}`
                }
                return query
            }, '')
            assocQuery = assocQuery.length ? `${assocQuery})` : ''
        }

        return assocQuery
    }

    /**
     * Generate a query like: AND NOT (uuid:xxx OR uuid:yyy...)
     *
     * @param {array} excludeItems
     *
     * @private
     *
     * @returns {string} generated query
     */
    _generateNotUuidsQuery(excludeItems) {
        const query = excludeItems.length ? excludeItems.reduce((query, item) => {
            return `${query}uuid:${item.id} OR `
        }, ' AND NOT (') : ''

        // Remove last " OR " and close the parenthesis
        const notQuery = `${query.slice(0, -4)})`
        return notQuery.trim().length > 1 ? notQuery : ''
    }

    /**
     * Search for concepts
     *
     * @param {object} args Objects with search-related properties
     *
     * optional Arg-props
     * @param {string|array} conceptTypes string or array, like 'x-im/story' or ['x-im/tag', 'x-im/category']
     * @param {string} term the term to look for in Concept name and description
     * @param {object} subtypes ['position', 'polygon']
     * @param {string} associatedWith x-im concept-type to which current type is associated, like 'x-im/channel'
     * @param {array|string} allowedStatuses Optional array with allowed statuses
     * @param {string} customQuery Optional string with OC query to be included in search (like: Active:false)
     *
     * @returns {array} response An array with result objects
     *
     * @example
     * const result = await ConceptService.searchForConceptSuggestions(
     *      conceptTypes: 'x-im/section',
     *      term: '*',
     *      subtypes: null,
     *      associatedWith: 'x-im/channel',
     *      allowedConceptStatuses = ["draft", "usable"]
     * )
    */
    async searchForConceptSuggestions(conceptTypes, term, subtypes, associatedWith, allowedConceptStatuses, customQuery) {
        let conceptTypeQuery = this._generateConceptTypeQuery(conceptTypes)
        let allowedStatusesQuery = this._generateAllowedStatusesQuery(allowedConceptStatuses || this.allowedConceptStatuses)
        let subtypesQuery = this._generateSubTypesQuery(subtypes)
        let assocQuery = ''

        // We need to remove characters that will break the search
        term = term.replace(/:|-/g, '*')

        if (associatedWith && associatedWith.length) {
            const articleConceptsOfAssociatedType = this.getArticleConceptsByType(associatedWith)

            if (!articleConceptsOfAssociatedType.length) {
                return []
            }

            assocQuery = this._generateAssocQuery(articleConceptsOfAssociatedType)
        }

        const titleSearchQuery = this.propertyMap.ConceptNameString ?
            ` AND (${this.propertyMap.ConceptName}:${term}* OR ${this.propertyMap.ConceptNameString}:${term}*)` :
            ` AND ${this.titleSearchField}:${term}*`

        const descriptionQuery = ` AND ${this.propertyMap.ConceptDefinitionShort}:*${term}*`

        /**
         * This is by design/request
         * This way items with term in title will be in the beginning of the list (sorted)
         * Followed by items with term in description (sorted)
         */
        customQuery = customQuery ? `${customQuery} AND ` : ''
        const titleItems = await this._search(`${customQuery}${conceptTypeQuery}${allowedStatusesQuery}${subtypesQuery}${assocQuery}${titleSearchQuery}`)
        const notQuery = titleSearchQuery.replace(' AND ', ' AND NOT ')

        const descriptionItems = await this._search(`${customQuery}${conceptTypeQuery}${allowedStatusesQuery}${subtypesQuery}${assocQuery}${descriptionQuery}${notQuery}`)
        const items = titleItems.concat(descriptionItems)

        // Rearrange props then filter out best matches and place them first in the array
        const exactMatches = []
        const theRest = []
        items.forEach(item => {
            const props = item.versions[0].properties
            const responseItem = this._convertProps(props)

            if (responseItem[this.propertyMap.ConceptName].toLowerCase().startsWith(term.toLowerCase())) {
                exactMatches.push(responseItem)
            } else {
                theRest.push(responseItem)
            }
        })

        return exactMatches.concat(theRest)
    }

    /**
     * Get concepts from remote storage by concept-type
     *
     * @param {string} conceptType eg x-im/category
     *
     * @returns {array} array of concepts with matching type
     */
    async getRemoteConceptsByType(conceptType) {
        const query = `${this.propertyMap.ConceptImTypeFull}:${conceptType}`
        const items = await this._search(query)

        return items.map(item => {
            let props = item.versions[0].properties
            return this._convertProps(props)
        })
    }

    /**
     * Search open content through ba-proxy
     *
     * @param {string} query
     *
     * @private
     *
     * @returns {array} searchResult
     */
    async _search(query) {
        const queryBuilder = new QueryBuilder({
            q: query,
            contenttype: this.contenttype
        })
            .setStart(0)
            .setLimit(this.searchLimit)
            .setSorting(this.sortField, true)
            .setResponseProperties(this.responseProperties)

        const builtQuery = queryBuilder.build()
        return await this._fetchItems(builtQuery)
    }
}

export {ConceptService}
